import { on } from '@ember/modifier';
/* eslint-disable ember/no-computed-properties-in-native-classes */
import { action, computed } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismIcon } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq, notEq } from 'ember-truth-helpers';
import HotelAndMarketModalsModalTrialIndicationSignUpToday from 'lumen/components/hotel-and-market/modals/modal-trial-indication-sign-up-today';
import trialModalAutoOpen from 'lumen/utils/hotel-and-market/trial-modal-auto-open';

interface HotelAndMarketTrialIndicationSignature {
  Args: {};
}

export default class HotelAndMarketTrialIndication extends Component<HotelAndMarketTrialIndicationSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare productState: Services['product-state'];

  @tracked isTrialIndicationModalVisible = false;

  get daysLeft(): number | undefined {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.productState.currentProductState?.days_left;
  }

  @computed(
    'hotelAndMarketService.hotelOrMarket.id',
    'isTrialIndicationModalVisible',
    'productState.currentProduct',
  )
  get hasUserSentTrialContactRequest(): boolean {
    return this.productState.hasSentTrialContactRequest();
  }

  get indicationColor(): string {
    const { daysLeft } = this;

    if (daysLeft === undefined) {
      return 'ota-trial-indication--dark-green';
    }

    if (daysLeft < 1) {
      return 'ota-trial-indication--red';
    }
    if (daysLeft < 3) {
      return 'ota-trial-indication--dark-orange';
    }
    if (daysLeft < 7) {
      return 'ota-trial-indication--light-orange';
    }
    if (daysLeft < 10) {
      return 'ota-trial-indication--light-green';
    }
    if (daysLeft < 14) {
      return 'ota-trial-indication--lighter-green';
    }

    return 'ota-trial-indication--dark-green';
  }

  @computed(
    'daysLeft',
    'productState.{currentProduct,currentProductState}',
    'subscriptionStatus',
  )
  get showTrialEndingWarning(): boolean {
    const { daysLeft } = this;

    if (daysLeft === undefined) {
      return false;
    }

    let showTrialEndingWarning = false;

    if (
      this.productState.currentProductState !== undefined &&
      this.subscriptionStatus === 'trial'
    ) {
      // don't show the indication if more than 45 days left
      if (daysLeft < 45) {
        showTrialEndingWarning = true;
      }

      if (trialModalAutoOpen(this.productState)) {
        this.onTrialIndicationClick();
      }
    }

    return showTrialEndingWarning;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get subscriptionStatus() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.productState.currentProductState?.subscription_status;
  }

  @action onTrialIndicationClick(): void {
    if (!this.hasUserSentTrialContactRequest) {
      this.isTrialIndicationModalVisible = true;
    }
  }

  @action onTrialIndicationClose(): void {
    this.isTrialIndicationModalVisible = false;
  }

  <template>
    {{! template-lint-disable require-presentational-children }}
    {{#if this.showTrialEndingWarning}}
      <div
        class="ota-trial-indication flex items-center gap-200
          {{this.indicationColor}}
          {{if this.hasUserSentTrialContactRequest 'request-sent'}}
          {{if (eq this.daysLeft 0) 'ota-trial-indication__expired'}}"
        role="button"
        {{on "click" this.onTrialIndicationClick}}
      >
        <PrismIcon @color="default-inverse" @name="clock" @size="small" />
        <span class="emphasis">
          {{#if (eq this.daysLeft 0)}}
            {{t "general.trial_indication.trial_ended"}}
          {{else if (notEq this.daysLeft undefined)}}
            {{t "general.trial_indication.days_left" count=this.daysLeft}}
          {{/if}}
        </span>
        &nbsp;
        {{#if this.hasUserSentTrialContactRequest}}
          <span class="ota-trial-indication__action-text semi-bold">
            {{t "general.trial_indication.contact_request_sent"}}
          </span>
        {{/if}}
      </div>
    {{/if}}

    {{#if this.isTrialIndicationModalVisible}}
      <HotelAndMarketModalsModalTrialIndicationSignUpToday
        @afterCloseAction={{this.onTrialIndicationClose}}
        {{! @glint-expect-error: Incorrect type }}
        @currentProduct={{this.productState.currentProduct}}
      />
    {{/if}}
  </template>
}
